import { parsePhoneNumber, CountryCode } from 'libphonenumber-js'

export interface NormalizePhoneOptions {
  defaultCountry?: CountryCode
  fallbackCountryCode?: string
}

export const formatPhoneNumberWithCountryCode = (
  phoneNumber: string,
  defaultCountry: CountryCode = 'AU',
  options: NormalizePhoneOptions = {},
): string => {
  try {
    // Remove common formatting characters but preserve the + if present
    const cleanedNumber = phoneNumber.replace(/[\s.\-()]/g, '')

    // If the number is empty after cleaning, throw an error
    if (!cleanedNumber) {
      throw new Error('Phone number cannot be empty')
    }

    let parsedNumber

    if (cleanedNumber.startsWith('+')) {
      // Already has + prefix - parse as international
      parsedNumber = parsePhoneNumber(cleanedNumber)
    } else {
      // Try to detect if this is an international number missing the + symbol
      // by attempting to parse with + prefix first
      let hasCountryCode = false

      try {
        // Test if adding + makes this a valid international number
        const testWithPlus = parsePhoneNumber(`+${cleanedNumber}`)
        if (testWithPlus && testWithPlus.isValid()) {
          // Check if this looks like an international number by length and pattern
          // Most international numbers with country code are 10-15 digits
          if (cleanedNumber.length >= 10 && cleanedNumber.length <= 15) {
            hasCountryCode = true
            parsedNumber = testWithPlus
          }
        }
      } catch {
        // If parsing with + fails, it's likely not an international number
        hasCountryCode = false
      }

      // If no country code detected, use the default country
      if (!hasCountryCode) {
        try {
          parsedNumber = parsePhoneNumber(cleanedNumber, defaultCountry)
        } catch {
          // If default country parsing fails, try one more time with + prefix
          // This handles edge cases where the number might still be international
          try {
            parsedNumber = parsePhoneNumber(`+${cleanedNumber}`)
          } catch {
            // Final fallback will be handled below
          }
        }
      }
    }

    // If parsing failed and we haven't tried all options, make one more attempt
    if (!parsedNumber || !parsedNumber.isValid()) {
      // Last resort: try parsing without any assumptions
      try {
        if (!cleanedNumber.startsWith('+')) {
          parsedNumber = parsePhoneNumber(`+${cleanedNumber}`)
        }
      } catch {
        // Ignore errors from this fallback attempt
      }
    }

    // If still not valid, throw an error
    if (!parsedNumber || !parsedNumber.isValid()) {
      throw new Error('Invalid phone number format')
    }

    // Return the number in E.164 format (international format with country code)
    return parsedNumber.format('E.164')
  } catch (error) {
    // Handle specific error cases
    if (error instanceof Error) {
      throw new Error(`Phone number normalization failed: ${error.message}`)
    }
    throw new Error('Invalid phone number')
  }
}

/**
 * Advanced phone number normalization with more options
 */
export const normalizePhoneNumber = (
  phoneNumber: string,
  options: NormalizePhoneOptions = {},
): {
  normalized: string
  country?: CountryCode
  isValid: boolean
  originalFormat: string
} => {
  const originalFormat = phoneNumber

  try {
    const defaultCountry = options.defaultCountry || 'US'
    const normalized = formatPhoneNumberWithCountryCode(
      phoneNumber,
      defaultCountry,
      options,
    )

    // Parse again to get country information
    const parsedNumber = parsePhoneNumber(normalized)

    return {
      normalized,
      country: parsedNumber?.country,
      isValid: true,
      originalFormat,
    }
  } catch (error) {
    return {
      normalized: phoneNumber,
      isValid: false,
      originalFormat,
    }
  }
}
